<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { Card } from '@taler/ui'
import { formatMoney, formatRate, impliedRate } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { TransferView } from '../internal/transfer-view.ts'

defineProps<{
  items: readonly TransferView[]
  isBusy: (transferId: string) => boolean
}>()

const emit = defineEmits<{ reverse: [transfer: TransferView] }>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')

function isFx(transfer: TransferView): boolean {
  return transfer.source.currency !== transfer.target.currency
}

function rateLabel(transfer: TransferView): string | undefined {
  if (!isFx(transfer)) return undefined
  const rate = impliedRate(transfer.source, transfer.target)
  return rate === undefined ? undefined : formatRate(rate, { locale: currentLocale.value })
}
</script>

<template>
  <section v-if="items.length > 0" class="flex flex-col gap-3">
    <h2 class="text-[length:var(--text-step-4)] font-semibold text-[color:var(--color-text)]">
      {{ t('transfers.recentlyAcceptedTitle') }}
    </h2>
    <ul class="flex flex-col gap-3">
      <li v-for="transfer in items" :key="transfer.id">
        <Card class="flex flex-wrap items-center justify-between gap-3">
          <div class="flex flex-col gap-1">
            <MoneyAmount :value="transfer.target" tone="transfer" />
            <p
              v-if="isFx(transfer)"
              class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
            >
              {{ t('transfers.fxLeg', { amount: formatMoney(transfer.source, { locale: currentLocale }) }) }}
              <template v-if="rateLabel(transfer)">
                · {{ t('transfers.impliedRate', { rate: rateLabel(transfer) }) }}
              </template>
            </p>
          </div>
          <div class="flex items-center gap-3">
            <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
              {{ t('transfers.reverseConsequence') }}
            </p>
            <button
              type="button"
              :disabled="isBusy(transfer.id)"
              class="min-h-[var(--density-target-min)] shrink-0 rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)] disabled:opacity-60"
              @click="emit('reverse', transfer)"
            >
              {{ t('transfers.reverse') }}
            </button>
          </div>
        </Card>
      </li>
    </ul>
  </section>
</template>
